import React, { useState } from "react";

import clsx from "clsx";

import attentiveLogo from "@assets/attentive-logo.svg";
import { LearnMoreTooltip } from "@features/sales-v3/top-level-filters/components/learn-more-tooltip";
import { ConnectionType } from "@north-beam/nb-client-side";
import { ConnectionCard } from "@pages/settings/connection-card";
import {
  ConnectionStatusType,
  type LoginComponentChildrenProps,
  type LoginComponentProps,
} from "@pages/settings/connection-status-manager";
import { PrimaryButton } from "@shared/buttons";

const type = ConnectionType.integration_attentive;

export function AttentiveMobileConnectionCard() {
  const successfulConnectionExtraText = React.useCallback(
    () => (
      <div className="italic flex flex-col">
        Now that you connected your account, do the following steps to finish
        the integration:
        <LearnMoreTooltip
          title="UTM Tracking"
          content="Add the UTM tracking in your Attentive Mobile account"
          link="https://docs.northbeam.io/docs/working-tracking-for-attentive-copy#this-guide-will-show-you-how-to-set-up-tracking-for-the-following-attentive-metrics-in-northbeam"
          overlayClassName="-translate-x-32"
        >
          <i className="fa-solid fa-circle-1 text-primary" /> UTM tracking
        </LearnMoreTooltip>
        <LearnMoreTooltip
          title="Daily logs"
          content="Configure the daily logs to send your data to Northbeam"
          link="https://docs.northbeam.io/docs/working-tracking-for-attentive-copy#step-3-set-up-daily-logs"
          overlayClassName="-translate-x-32"
        >
          <i className="fa-solid fa-circle-2 text-primary" /> Daily logs
        </LearnMoreTooltip>
        <LearnMoreTooltip
          title="Custom events for Attribution"
          content="Add the Northbeam script to start sending your custom events, used for Attribution"
          link="https://docs.northbeam.io/docs/working-tracking-for-attentive-copy#step-5-implement-sign-up-event"
          overlayClassName="-translate-x-32"
        >
          <i className="fa-solid fa-circle-3 text-primary" /> Custom events
        </LearnMoreTooltip>
      </div>
    ),
    [],
  );
  return (
    <ConnectionCard
      type={type}
      connectionTypeName="Attentive Mobile"
      connectionCallToAction="Integrate your Attentive Mobile account"
      logoUrl={attentiveLogo}
      logoAltText="Attentive Logo"
      successfulConnectionExtraText={successfulConnectionExtraText}
      knowledgeBaseLink="https://docs.northbeam.io/docs/working-tracking-for-attentive-copy"
    >
      {({
        upsertConnection,
        isLoading,
        accounts,
        setStatus,
      }: LoginComponentChildrenProps) => (
        <AttentiveLoginComponent
          upsertConnection={upsertConnection}
          setStatus={setStatus}
          isLoading={isLoading}
          accounts={accounts}
        />
      )}
    </ConnectionCard>
  );
}

function AttentiveLoginComponent({
  setStatus,
  upsertConnection,
  isLoading,
}: Omit<LoginComponentProps, "integrationDetails">) {
  const [sftpUsername, setSftpUsername] = useState("");
  const [sftpPassword, setSftpPassword] = useState("");
  const [displayPassword, setDisplayPassword] = useState(false);

  const togglePasswordVisibility = () =>
    setDisplayPassword((previousDisplayPassword) => !previousDisplayPassword);

  const onConnectClicked = React.useCallback(async () => {
    const result = await upsertConnection({
      sftp_username: sftpUsername.trim(),
      sftp_password: sftpPassword?.trim(),
    });
    const connectionLevelIssues =
      result?.data?.upsertConnection?.connection?.connectionValidation
        ?.connectionLevelIssues ?? [];

    if (connectionLevelIssues.length > 0) {
      setStatus({
        type: ConnectionStatusType.HAS_ERROR,
        errorMessage: connectionLevelIssues[0].message,
      });
    }
  }, [sftpPassword, sftpUsername, upsertConnection, setStatus]);

  return (
    <div className="mb-3">
      <div className="flex flex-col gap-y-5 mt-5">
        <div>
          <div>
            SFTP Username{" "}
            <LearnMoreTooltip
              title="SFTP Username"
              content="Provide your Attentive Mobile SFTP Username"
              link="https://docs.northbeam.io/docs/working-tracking-for-attentive-copy#step-1-create-an-attentive-sftp-account"
            />
          </div>
          <input
            disabled={isLoading}
            type="text"
            className="form-control max-w-xl"
            value={sftpUsername}
            placeholder="SFTP Username..."
            id="sftpUsername"
            onChange={(event) => setSftpUsername(event.target.value)}
          />
        </div>
        <div>
          <div>
            SFTP Password{" "}
            <LearnMoreTooltip
              title="SFTP Password"
              content="Provide your Attentive Mobile SFTP Password"
              link="https://docs.northbeam.io/docs/working-tracking-for-attentive-copy#step-1-create-an-attentive-sftp-account"
            />
          </div>
          <div className="flex items-center gap-2 max-w-xl relative">
            <input
              disabled={isLoading}
              type={displayPassword ? "text" : "password"}
              className="form-control"
              value={sftpPassword}
              placeholder="SFTP Password..."
              id="sftpPassword"
              onChange={(event) => setSftpPassword(event.target.value)}
            />
            <span
              className="absolute right-2 text-interstellar-1"
              onClick={togglePasswordVisibility}
            >
              <i
                className={clsx(
                  "fas",
                  displayPassword ? "fa-eye-slash" : "fa-eye",
                )}
              ></i>
            </span>
          </div>
        </div>
        <div>
          <PrimaryButton
            loading={isLoading}
            disabled={!sftpUsername.trim() || !sftpPassword?.trim()}
            onClick={onConnectClicked}
          >
            {isLoading ? "Please Wait..." : "CONNECT"}
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
}
